import React, { useEffect } from 'react'
import copy from 'copy-to-clipboard'

import { routes, useFlag, useFolderTerm } from '@sketch/modules-common'

import { Dropdown, useModalContext } from '@sketch/components'

import { useToast } from '@sketch/toasts'

import {
  ProjectFragment,
  TrashedProjectFragment,
  useLeaveProjectMutation,
  WorkspaceMinimalFragment,
  usePinProjectMutation,
  useUnpinProjectMutation,
  useApolloClient,
  GetProjectDetailsQuery,
  GetProjectDetailsQueryVariables,
  GetProjectDetailsDocument,
  NestedProjectFragment,
  useGetProjectDetailsQuery,
} from '@sketch/gql-types'

import DeleteProject from '../../modals/DeleteProject'
import DeleteProjectPermanently from '../../modals/DeleteProjectPermanently'
import RestoreProject from '../../modals/RestoreProject'
import { ProjectSharingModal } from '../../modals/ProjectSharingModal'
import { CreateCollectionModal } from '../../../collections/modals'
import { CreateNestedProjectModal, EditProjectModal } from '../../modals'
import { ConfirmRemoveMemberModal } from '../RemoveMemberModal'
import { ProjectArchivedDropdownProps } from '../ProjectDropdown/ProjectArchivedDropdown'

import { MoveStorageItemModal } from '../../../storageItems'

interface ProjectProps {
  project:
    | Pick<
        ProjectFragment | NestedProjectFragment,
        | '__typename'
        | 'identifier'
        | 'name'
        | 'deletedAt'
        | 'parentProjectIdentifier'
      >
    | Pick<
        TrashedProjectFragment,
        '__typename' | 'identifier' | 'name' | 'deletedAt'
      >
}

interface ProjectAndWorkspaceProps extends ProjectProps {
  workspaceId: string
}

interface EditProjectActionProps {
  workspaceId: string
  project: Pick<ProjectFragment, 'identifier' | 'name' | 'description'>
}

interface RenameProjectActionProps {
  onRename: () => void
}

interface LeaveProjectActionProps {
  workspaceIdentifier: string
  projectIdentifier: string
  projectName: string
}

type PinProjectProps = {
  pinned?: string | null
  projectIdentifier: string
  workspaceId: string
}

export const CreateCollectionAction = ({
  project,
  workspaceIdentifier,
}: {
  project: ProjectFragment
  workspaceIdentifier: string
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      onClick={() => {
        showModal(CreateCollectionModal, {
          project,
          workspaceIdentifier,
        })
      }}
    >
      Create Collection&hellip;
    </Dropdown.Item>
  )
}

export const CreateNestedProjectAction = ({
  project,
  workspaceIdentifier,
}: {
  project: ProjectFragment
  workspaceIdentifier: string
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      onClick={() => {
        showModal(CreateNestedProjectModal, {
          project,
          workspaceIdentifier,
        })
      }}
    >
      Create a Folder&hellip;
    </Dropdown.Item>
  )
}

export const MoveProjectAction = ({
  project,
  workspaceIdentifier,
}: {
  project: ProjectFragment
  workspaceIdentifier: string
}) => {
  const { showModal } = useModalContext()

  const client = useApolloClient()

  useEffect(() => {
    // prefetch project details to avoid loading them in the modal
    client.query<GetProjectDetailsQuery, GetProjectDetailsQueryVariables>({
      query: GetProjectDetailsDocument,
      variables: { projectIdentifier: project.identifier },
    })
  }, [client, project.identifier])

  return (
    <Dropdown.Item
      onClick={() => {
        showModal(MoveStorageItemModal, {
          item: project,
          workspaceIdentifier,
          parentProjectIdentifier: project.parentProjectIdentifier,
          originType: 'standard',
        })
      }}
    >
      Move To&hellip;
    </Dropdown.Item>
  )
}

export const LeaveProjectAction: React.FC<LeaveProjectActionProps> = ({
  workspaceIdentifier,
  projectIdentifier,
  projectName,
}) => {
  const { showModal, hideModal } = useModalContext()
  const [leaveProject] = useLeaveProjectMutation({
    variables: { projectIdentifier },
    onError() {
      // onError is used in `<RemoveMemberButton .../>` to handle the case
      // where a user is removing themselves we must show them the confirmation
      // dialog first. All other errors are ignored there.
      //
      // Since we are showing the confirmation dialog in all circumstances (and
      // providing `confirmed: true`) it is not necessary to handle this error
      // case.
      return
    },
  })

  const onConfirm = async () => {
    await leaveProject()
    hideModal()
  }

  return (
    <Dropdown.Item
      intent="negative"
      onClick={() => {
        showModal(ConfirmRemoveMemberModal, {
          projectName,
          projectIdentifier,
          workspaceIdentifier,
          onConfirm,
          onCancelAction: hideModal,
        })
      }}
    >
      Leave&hellip;
    </Dropdown.Item>
  )
}
export const DeleteProjectAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      intent="negative"
      onClick={() => {
        showModal(DeleteProject, {
          projectId: project.identifier,
          projectName: project.name,
          workspaceId,
        })
      }}
    >
      Delete&hellip;
    </Dropdown.Item>
  )
}

export const EditProjectAction: React.FC<EditProjectActionProps> = ({
  workspaceId,
  project,
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      data-option-item
      onClick={() =>
        showModal(EditProjectModal, {
          project,
          workspaceIdentifier: workspaceId,
        })
      }
    >
      Edit&hellip;
    </Dropdown.Item>
  )
}

export const RenameProjectAction: React.FC<RenameProjectActionProps> = ({
  onRename,
}) => (
  <Dropdown.Item data-option-item onClick={onRename}>
    Rename&hellip;
  </Dropdown.Item>
)

export const CopyProjectAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showToast } = useToast()

  return (
    <Dropdown.Item
      onClick={() => {
        const projectLink = (
          project.deletedAt
            ? routes.WORKSPACE_TRASH_PROJECT
            : routes.WORKSPACE_PROJECT
        ).create({
          workspaceId,
          projectId: project.identifier,
        })

        copy(`${window.location.origin}${projectLink}`)
        showToast('Link copied')
      }}
    >
      Copy Link
    </Dropdown.Item>
  )
}

export const RestoreProjectAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showModal } = useModalContext()
  const isNestedProjectsOn = useFlag('nested-projects')

  // Preload project details, we don't need them in the dropdown, but we need them
  // in the Project Picker modal, so we preload for faster user experience.
  useGetProjectDetailsQuery({
    variables: { projectIdentifier: project.identifier },
  })

  return (
    <Dropdown.Item
      onClick={() =>
        isNestedProjectsOn
          ? showModal(MoveStorageItemModal, {
              originType: 'stashed',
              item: { __typename: 'Project', identifier: project.identifier },
              workspaceIdentifier: workspaceId,
              parentProjectIdentifier:
                // TODO: remove this `as` casting once we will cleanup isNestedProjectsOn FF
                (project as NestedProjectFragment | undefined)
                  ?.parentProjectIdentifier || null,
            })
          : showModal(RestoreProject, { project, workspaceId })
      }
    >
      Restore&hellip;
    </Dropdown.Item>
  )
}

export const DeleteProjectPermanentlyAction: React.FC<
  ProjectAndWorkspaceProps
> = ({ project, workspaceId }) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      intent="negative"
      onClick={() =>
        showModal(DeleteProjectPermanently, { project, workspaceId })
      }
    >
      Delete Permanently&hellip;
    </Dropdown.Item>
  )
}

export const ShareProjectAction: React.FC<{
  project: ProjectArchivedDropdownProps['project']
  workspace: WorkspaceMinimalFragment
}> = ({ project, workspace }) => {
  const { showModal } = useModalContext()

  const handleShareClick = () => {
    showModal(ProjectSharingModal, {
      project,
      workspace,
    })
  }

  return <Dropdown.Item onClick={handleShareClick}>Share&hellip;</Dropdown.Item>
}

export const PinProjectAction = ({
  pinned,
  projectIdentifier,
  workspaceId,
}: PinProjectProps) => {
  const [pinProject] = usePinProjectMutation({
    variables: {
      projectIdentifier,
    },
    onError: 'show-toast',
    optimisticResponse: {
      __typename: 'RootMutationType',
      pinProject: {
        __typename: 'PinProjectResponse',
        project: {
          __typename: 'Project',
          identifier: projectIdentifier,
        },
      },
    },
  })

  const [unpinProject] = useUnpinProjectMutation({
    variables: {
      projectIdentifier,
    },
    onError: 'show-toast',
    optimisticResponse: {
      __typename: 'RootMutationType',
      unpinProject: {
        __typename: 'UnpinProjectResponse',
        project: {
          __typename: 'Project',
          identifier: projectIdentifier,
        },
      },
    },
  })

  const folderTerm = useFolderTerm()

  if (pinned) {
    return (
      <Dropdown.Item
        onClick={() => unpinProject({ variables: { projectIdentifier } })}
      >
        Unpin {folderTerm.upper}
      </Dropdown.Item>
    )
  }

  return (
    <Dropdown.Item
      onClick={() => pinProject({ variables: { projectIdentifier } })}
    >
      Pin {folderTerm.upper}
    </Dropdown.Item>
  )
}
